import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import { Button, LoadingSkeleton, EmptyState } from "../../../components/ui";
import { getFlatById } from "../../../services/flatsService";
import { buildCommercialStaffRoster, formatFloorName } from "../../../utils/propertyStructure";
import styles from "./StoreDetail.module.css";
import {
  ArrowLeft,
  Store,
  UserCheck,
  CreditCard,
  ShoppingBag,
  Calculator,
  Headphones,
  Package,
  Layers,
  RefreshCw,
  Building2,
  Mail,
  Phone
} from "lucide-react";

const StoreDetail = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyIdParam = searchParams.get("propertyId") || "";
  const towerIdParam = searchParams.get("towerId") || "";

  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchUnit = useCallback(async (isManualRefresh = false) => {
    if (!unitId) return;
    if (isManualRefresh && unit) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const data = await getFlatById(unitId);
      setUnit(data);
    } catch (err) {
      console.error("Failed to load store unit:", err);
      setError(err?.data?.error || err?.message || "Failed to load store details.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [unitId, unit]);

  useEffect(() => {
    fetchUnit(false);
  }, [unitId]);

  const assignments = unit?.assignments || [];

  const roster = useMemo(() => {
    return buildCommercialStaffRoster(assignments);
  }, [assignments]);

  const societyId = unit?.society_id || propertyIdParam;
  const towerId = unit?.tower_id || towerIdParam;
  const societyName = unit?.society_name || "Commercial Property";
  const towerName = unit?.tower_name || "Commercial Wing";

  const handleBack = () => {
    if (societyId && towerId) {
      navigate(`/properties?propertyId=${societyId}&towerId=${towerId}`);
    } else if (societyId) {
      navigate(`/properties?propertyId=${societyId}`);
    } else {
      navigate(`/properties`);
    }
  };

  const isMultiStorey = String(unit?.flat_number || "").includes("+") ||
    String(unit?.unit_type || "").toLowerCase().includes("multi") ||
    String(unit?.unit_type || "").toLowerCase().includes("duplex");

  return (
    <AdminLayout title="Commercial Store & Staff Roster">
      <div className={styles.pageContainer}>
        {/* Navigation Bar */}
        <div className={styles.breadcrumbBar}>
          <Button variant="outline" size="sm" onClick={handleBack} icon={<ArrowLeft size={16} />}>
            Back to Units in {towerName}
          </Button>

          <div className={styles.breadcrumbTrail}>
            <span className={styles.breadcrumbItem} onClick={() => navigate("/properties")}>Properties</span>
            <span>/</span>
            <span className={styles.breadcrumbItem} onClick={() => navigate(`/properties?propertyId=${societyId}`)}>{societyName}</span>
            <span>/</span>
            <span className={styles.breadcrumbItem} onClick={handleBack}>{towerName}</span>
            <span>/</span>
            <span className={styles.breadcrumbActive}>
              {unit?.flat_number ? unit.flat_number : (loading ? <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><span style={{ width: 14, height: 14, border: "2px solid #cbd5e1", borderTopColor: "var(--color-dark-bg, #27306f)", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} /> Loading</span> : "Store")}
            </span>
          </div>
        </div>

        {loading && !unit ? (
          <div className={styles.skeletonContainer}>
            {/* Header Hero Banner Skeleton */}
            <div className={styles.headerBanner}>
              <div className={styles.bannerLeft}>
                <LoadingSkeleton width="56px" height="56px" borderRadius="14px" />
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <LoadingSkeleton width="240px" height="24px" />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <LoadingSkeleton width="90px" height="20px" />
                    <LoadingSkeleton width="180px" height="20px" />
                  </div>
                </div>
              </div>
              <div className={styles.bannerStats}>
                <LoadingSkeleton width="88px" height="58px" borderRadius="12px" />
              </div>
            </div>

            {/* Store Manager Skeleton */}
            <div className={styles.section}>
              <LoadingSkeleton width="200px" height="24px" style={{ marginBottom: "12px" }} />
              <LoadingSkeleton width="100%" height="90px" borderRadius="12px" />
            </div>

            {/* Store Staff Grid Skeleton */}
            <div className={styles.section}>
              <LoadingSkeleton width="200px" height="24px" style={{ marginBottom: "12px" }} />
              <div className={styles.skeletonStaffGrid}>
                <LoadingSkeleton width="100%" height="120px" borderRadius="12px" />
                <LoadingSkeleton width="100%" height="120px" borderRadius="12px" />
                <LoadingSkeleton width="100%" height="120px" borderRadius="12px" />
              </div>
            </div>
          </div>
        ) : error || !unit ? (
          <div className={styles.notFoundCard}>
            <div className={styles.notFoundIconCircle}>
              <Building2 size={36} color="#64748b" />
            </div>
            <h2 className={styles.notFoundTitle}>Commercial Store Not Found</h2>
            <p className={styles.notFoundDesc}>
              {error || `Commercial unit #${unitId} could not be located in ${towerName}. It may have been reorganized.`}
            </p>
            <div className={styles.notFoundActions}>
              <Button variant="primary" onClick={handleBack} icon={<ArrowLeft size={16} />}>
                Return to {towerName}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/properties?propertyId=${societyId}`)}
              >
                View All Property Units
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Header Hero Banner */}
            <div className={styles.headerBanner}>
              {refreshing && <div className={styles.refreshProgressBar} />}
              <div className={styles.bannerLeft}>
                <div className={styles.storeIconCircle}>
                  <Store size={32} />
                </div>
                <div className={styles.storeTitleInfo}>
                  <h1 className={styles.storeTitle}>{unit.flat_number}</h1>
                  <div className={styles.storeMetaRow}>
                    <span className={styles.storeCategoryBadge}>
                      {unit.unit_type || "Shop / Retail"}
                    </span>
                    <span className={styles.storeFloorBadge}>
                      {formatFloorName(unit.floor_number)} • {towerName} ({societyName})
                    </span>
                    {isMultiStorey && (
                      <span className={styles.multiStoreyBadge}>
                        <Layers size={13} style={{ marginRight: "4px", verticalAlign: "middle" }} />
                        Multi-Storey Store
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.bannerStats}>
                <div className={styles.statBox}>
                  <div className={styles.statVal}>{roster.totalStaff}</div>
                  <div className={styles.statLbl}>Store Staff</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchUnit(true)}
                  title="Refresh Store Data"
                  loading={refreshing}
                  icon={<RefreshCw size={14} className={refreshing ? styles.spin : ""} />}
                />
              </div>
            </div>

            {/* Store Manager / Owner Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>
                  <UserCheck size={18} color="#0f766e" />
                  Store Manager / Franchisee
                </div>
              </div>
              {roster.manager ? (
                <div className={[styles.staffCard, styles.staffCardLead].join(" ")}>
                  <div className={styles.staffHeader}>
                    <div className={styles.staffName}>{roster.manager.full_name}</div>
                    <span className={[styles.staffRoleBadge, styles.staffRoleBadgeManager].join(" ")}>
                      Store Manager
                    </span>
                  </div>
                  <div className={styles.staffDesignation}>
                    Designation (Data Field): {roster.manager.designation || "Store General Manager"}
                  </div>
                  <div className={styles.staffContact}>
                    {roster.manager.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {roster.manager.email_id}</div>}
                    {roster.manager.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {roster.manager.mobile_number}</div>}
                  </div>
                </div>
              ) : (
                <div style={{ padding: "16px", color: "#64748b", fontSize: "14px", background: "white", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                  No Store Manager assigned.
                </div>
              )}
            </div>

            {/* Cashier & Billing Staff */}
            {roster.cashiers.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <CreditCard size={18} color="#b45309" />
                    Cashier & Billing Desk ({roster.cashiers.length})
                  </div>
                </div>
                <div className={styles.rosterGrid}>
                  {roster.cashiers.map((c) => (
                    <div key={c.user_id} className={styles.staffCard}>
                      <div className={styles.staffHeader}>
                        <div className={styles.staffName}>{c.full_name}</div>
                        <span className={styles.staffRoleBadge}>{c.system_role || "Cashier"}</span>
                      </div>
                      <div className={styles.staffDesignation}>
                        Designation: {c.designation || "Lead Cashier"}
                      </div>
                      <div className={styles.staffContact}>
                        {c.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {c.email_id}</div>}
                        {c.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {c.mobile_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sales & Counter Staff */}
            {roster.sales.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <ShoppingBag size={18} color="var(--color-dark-bg)" />
                    Sales Executives & Floor Assistants ({roster.sales.length})
                  </div>
                </div>
                <div className={styles.rosterGrid}>
                  {roster.sales.map((s) => (
                    <div key={s.user_id} className={styles.staffCard}>
                      <div className={styles.staffHeader}>
                        <div className={styles.staffName}>{s.full_name}</div>
                        <span className={styles.staffRoleBadge}>{s.system_role || "Sales Staff"}</span>
                      </div>
                      <div className={styles.staffDesignation}>
                        Designation: {s.designation || "Floor Sales Associate"}
                      </div>
                      <div className={styles.staffContact}>
                        {s.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {s.email_id}</div>}
                        {s.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {s.mobile_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accountants */}
            {roster.accountants.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <Calculator size={18} color="#7c3aed" />
                    Store Accountants & Auditors ({roster.accountants.length})
                  </div>
                </div>
                <div className={styles.rosterGrid}>
                  {roster.accountants.map((a) => (
                    <div key={a.user_id} className={styles.staffCard}>
                      <div className={styles.staffHeader}>
                        <div className={styles.staffName}>{a.full_name}</div>
                        <span className={styles.staffRoleBadge}>{a.system_role || "Accountant"}</span>
                      </div>
                      <div className={styles.staffDesignation}>
                        Designation: {a.designation || "Store Accountant"}
                      </div>
                      <div className={styles.staffContact}>
                        {a.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {a.email_id}</div>}
                        {a.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {a.mobile_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Front Desk Receptionists */}
            {roster.receptionists.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <Headphones size={18} color="#059669" />
                    Store Reception & Helpdesk ({roster.receptionists.length})
                  </div>
                </div>
                <div className={styles.rosterGrid}>
                  {roster.receptionists.map((r) => (
                    <div key={r.user_id} className={styles.staffCard}>
                      <div className={styles.staffHeader}>
                        <div className={styles.staffName}>{r.full_name}</div>
                        <span className={styles.staffRoleBadge}>{r.system_role || "Receptionist"}</span>
                      </div>
                      <div className={styles.staffDesignation}>
                        Designation: {r.designation || "Store Receptionist"}
                      </div>
                      <div className={styles.staffContact}>
                        {r.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {r.email_id}</div>}
                        {r.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {r.mobile_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Helpers & Inventory Staff */}
            {roster.helpers.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <Package size={18} color="#d97706" />
                    Stock, Inventory & Store Helpers ({roster.helpers.length})
                  </div>
                </div>
                <div className={styles.rosterGrid}>
                  {roster.helpers.map((h) => (
                    <div key={h.user_id} className={styles.staffCard}>
                      <div className={styles.staffHeader}>
                        <div className={styles.staffName}>{h.full_name}</div>
                        <span className={styles.staffRoleBadge}>{h.system_role || "Helper"}</span>
                      </div>
                      <div className={styles.staffDesignation}>
                        Designation: {h.designation || "Store Assistant"}
                      </div>
                      <div className={styles.staffContact}>
                        {h.email_id && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Mail size={13} /> {h.email_id}</div>}
                        {h.mobile_number && <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Phone size={13} /> {h.mobile_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {roster.totalStaff === 0 && (
              <div style={{ marginTop: "16px" }}>
                <EmptyState
                  icon={<Store size={44} />}
                  title="No Store Staff Assigned"
                  description={`No staff members or managers are currently assigned to ${unit.flat_number}. Staff assigned through Property Operations will appear organized by department and role here.`}
                  action={
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      icon={<ArrowLeft size={16} />}
                    >
                      Back to {towerName}
                    </Button>
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default StoreDetail;
